import { Upload, Folder, RefreshCw } from 'lucide-react'
import React, { useState, useRef, useCallback, useEffect } from 'react'

import LoadingSpinner from '../common/LoadingSpinner'

import FileCard from './FileCard'

import { storageService } from '@/services'
import type {
  FileUploadResponse,
  FileInfoResponse
} from '@/services/StorageService'
import { isMobileDevice } from '@/utils/platform'

export interface FileUploadItem {
  id: string
  file: File
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  error?: string
  response?: FileUploadResponse
}

interface MultiFileUploaderProps {
  /** 文件选择回调 */
  onFilesChange?: (files: FileUploadItem[]) => void
  /** 上传完成回调 */
  onUploadComplete?: (results: FileUploadResponse[]) => void
  /** 接受的文件类型 */
  accept?: string
  /** 最大文件大小 (bytes) */
  maxSize?: number
  /** 最大文件数量 */
  maxFiles?: number
  /** 是否禁用 */
  disabled?: boolean
  /** 自定义样式类 */
  className?: string
  /** 文件夹路径（用于上传和浏览） */
  folder?: string
  /** 是否自动上传 */
  autoUpload?: boolean
  /** 是否启用上传功能 */
  enableUpload?: boolean
  /** 文件删除回调 */
  onFileDelete?: (fileKey: string) => void
  /** 文件下载回调 */
  onFileDownload?: (fileInfo: FileInfoResponse) => void
}

const MultiFileUploader: React.FC<MultiFileUploaderProps> = ({
  onFilesChange,
  onUploadComplete,
  accept = '*/*',
  maxSize = 10 * 1024 * 1024, // 10MB
  maxFiles = 10,
  disabled = false,
  className = '',
  folder = 'uploads',
  autoUpload = false,
  enableUpload = true,
  onFileDelete,
  onFileDownload
}) => {
  const [files, setFiles] = useState<FileUploadItem[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [existingFiles, setExistingFiles] = useState<FileInfoResponse[]>([])
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)

  useEffect(() => {
    setIsMobile(isMobileDevice())

    // 添加窗口resize监听
    const handleResize = () => {
      setIsMobile(isMobileDevice())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    onFilesChange?.(files)
  }, [files, onFilesChange])

  // 加载现有文件列表
  const loadExistingFiles = useCallback(async () => {
    if (!enableUpload) return

    setIsLoadingFiles(true)
    try {
      const response = await storageService.listFiles(folder, 100, {
        params: {
          include_metadata: true
        }
      })
      setExistingFiles(response.files)
    } catch (error) {
      console.error('加载文件列表失败:', error)
      setExistingFiles([])
    } finally {
      setIsLoadingFiles(false)
    }
  }, [enableUpload, folder])

  useEffect(() => {
    loadExistingFiles()
  }, [loadExistingFiles])

  const generateFileId = () =>
    `file_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`

  const validateFile = (file: File): string | null => {
    // 检查文件类型
    if (accept !== '*/*') {
      const allowedTypes = accept.split(',').map((type) => type.trim())
      const isValidType = allowedTypes.some((type) => {
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type.toLowerCase())
        }
        return file.type === type || file.type.startsWith(type.replace('*', ''))
      })

      if (!isValidType) {
        return `文件类型不支持，支持的格式: ${accept}`
      }
    }

    // 检查文件大小
    if (file.size > maxSize) {
      return `文件大小不能超过 ${(maxSize / 1024 / 1024).toFixed(1)}MB`
    }

    return null
  }

  const addFiles = useCallback(
    (newFiles: File[]) => {
      const validFiles: FileUploadItem[] = []

      for (const file of newFiles) {
        // 检查文件数量限制
        if (files.length + validFiles.length >= maxFiles) {
          break
        }

        // 检查是否已存在同名文件（在新上传文件中）
        const existingInFiles = files.find(
          (f) => f.file.name === file.name && f.file.size === file.size
        )
        if (existingInFiles) {
          continue
        }

        // 检查是否与现有服务器文件重复
        const existingInServer = existingFiles.some((existing) => {
          const existingFileName =
            (existing.metadata?.original_filename as string) ||
            existing.key.split('/').pop() ||
            ''
          return (
            existingFileName.toLowerCase() === file.name.toLowerCase() &&
            existing.size === file.size
          )
        })
        if (existingInServer) {
          continue
        }

        const error = validateFile(file)
        const fileItem: FileUploadItem = {
          id: generateFileId(),
          file,
          status: error ? 'error' : 'pending',
          progress: 0,
          error: error || undefined
        }

        validFiles.push(fileItem)
      }

      setFiles((prev) => [...prev, ...validFiles])

      // 自动上传
      if (autoUpload && enableUpload) {
        validFiles.forEach((fileItem) => {
          if (fileItem.status === 'pending') {
            uploadFile(fileItem.id)
          }
        })
      }
    },
    [files, maxFiles, autoUpload, enableUpload]
  )

  const uploadFile = async (fileId: string) => {
    if (!enableUpload) return

    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId ? { ...f, status: 'uploading' as const } : f
      )
    )

    const fileItem = files.find((f) => f.id === fileId)
    if (!fileItem) return

    try {
      const response = await storageService.uploadFileWithProgress(
        fileItem.file,
        folder,
        (progress) => {
          setFiles((prev) =>
            prev.map((f) => (f.id === fileId ? { ...f, progress } : f))
          )
        }
      )

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, status: 'success' as const, progress: 100, response }
            : f
        )
      )

      // 上传成功后，重新加载文件列表以获取最新状态
      if (response.file_key) {
        // 延迟一下重新加载，确保服务器已经处理完成
        setTimeout(() => {
          loadExistingFiles()
        }, 500)
        setFiles((prev) => prev.filter((f) => f.id !== fileId))
      }

      // 检查是否所有文件都上传完成
      const updatedFiles = files.filter((f) => f.id !== fileId)
      const allCompleted = updatedFiles.every(
        (f) => f.status === 'success' || f.status === 'error'
      )
      if (allCompleted) {
        const successfulUploads = [response]
        onUploadComplete?.(successfulUploads)
      }
    } catch (error) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                status: 'error' as const,
                error: error instanceof Error ? error.message : '上传失败'
              }
            : f
        )
      )
    }
  }

  const removeFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId))
  }

  const retryUpload = (fileId: string) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId
          ? { ...f, status: 'pending' as const, progress: 0, error: undefined }
          : f
      )
    )

    if (autoUpload && enableUpload) {
      uploadFile(fileId)
    }
  }

  const uploadAllFiles = () => {
    if (!enableUpload) return

    files.forEach((fileItem) => {
      if (fileItem.status === 'pending' || fileItem.status === 'error') {
        uploadFile(fileItem.id)
      }
    })
  }

  const clearAllFiles = () => {
    setFiles([])
  }

  // 下载现有文件
  const handleDownloadFile = async (fileInfo: FileInfoResponse) => {
    try {
      if (onFileDownload) {
        onFileDownload(fileInfo)
      } else {
        // 默认下载行为
        const blob = await storageService.downloadFile(fileInfo.key)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download =
          (fileInfo.metadata?.original_filename as string) ||
          fileInfo.key.split('/').pop() ||
          'download'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('下载文件失败:', error)
    }
  }

  // 删除现有文件
  const handleDeleteFile = async (fileInfo: FileInfoResponse) => {
    try {
      if (onFileDelete) {
        onFileDelete(fileInfo.key)
      } else {
        // 默认删除行为
        await storageService.deleteFile(fileInfo.key)
        // 重新加载文件列表
        loadExistingFiles()
      }
    } catch (error) {
      console.error('删除文件失败:', error)
    }
  }

  // 拖拽事件处理
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    if (disabled) return

    dragCounterRef.current++
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current--

    if (dragCounterRef.current === 0) {
      setIsDragOver(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    if (disabled) return

    dragCounterRef.current = 0
    setIsDragOver(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    addFiles(droppedFiles)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    addFiles(selectedFiles)

    // 重置input值以便重新选择相同文件
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  if (isLoadingFiles) {
    return <LoadingSpinner />
  }

  return (
    <div className={`w-full ${className}`}>
      {/* 文件夹信息和操作 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Folder className="w-5 h-5 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">
            {folder || '根目录'}
          </h3>
        </div>
        <button
          onClick={loadExistingFiles}
          disabled={isLoadingFiles}
          className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          <RefreshCw
            className={`w-4 h-4 ${isLoadingFiles ? 'animate-spin' : ''}`}
          />
          <span>刷新</span>
        </button>
      </div>

      {/* 文件列表 */}
      <div
        className={`${
          isMobile
            ? 'space-y-3'
            : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'
        }`}
      >
        {/* 显示现有文件 */}
        {existingFiles.map((fileInfo) => (
          <FileCard
            key={`existing_${fileInfo.key}`}
            fileInfo={fileInfo}
            onDownload={() => handleDownloadFile(fileInfo)}
            onDelete={() => handleDeleteFile(fileInfo)}
            isMobile={isMobile}
          />
        ))}

        {/* 显示新上传的文件 */}
        {files.map((fileItem) => (
          <FileCard
            key={fileItem.id}
            file={fileItem.file}
            status={fileItem.status}
            progress={fileItem.progress}
            error={fileItem.error}
            onRemove={() => removeFile(fileItem.id)}
            onRetry={() => retryUpload(fileItem.id)}
            onUpload={
              !autoUpload && enableUpload
                ? () => uploadFile(fileItem.id)
                : undefined
            }
            disabled={disabled}
            isMobile={isMobile}
          />
        ))}

        {/* 上传区域作为列表的最后一项 */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`
            relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all duration-200
            ${
              isMobile
                ? 'flex items-center space-x-3'
                : 'flex flex-col items-center justify-center'
            }
            ${
              isDragOver
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={accept}
            onChange={handleFileInputChange}
            className="hidden"
            disabled={disabled}
          />

          {
            // PC端布局
            <div className="flex items-center space-x-4">
              <div
                className={`
                w-12 h-12 rounded-full flex items-center justify-center
                ${
                  isDragOver
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600'
                }
              `}
              >
                <Upload className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500">
                  {accept === '*/*'
                    ? '所有格式'
                    : accept.replace(/\./g, '').toUpperCase()}
                </p>
                <p className="text-xs text-gray-500">
                  最大 {(maxSize / 1024 / 1024).toFixed(1)}MB
                </p>
              </div>
            </div>
          }
        </div>
      </div>

      {/* 批量操作按钮 */}
      {files.length > 0 && (
        <div className="mt-4 flex justify-center space-x-2">
          {!autoUpload && enableUpload && (
            <button
              onClick={uploadAllFiles}
              disabled={
                disabled ||
                files.every(
                  (f) => f.status === 'success' || f.status === 'uploading'
                )
              }
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上传全部 (
              {
                files.filter(
                  (f) => f.status === 'pending' || f.status === 'error'
                ).length
              }
              )
            </button>
          )}
          <button
            onClick={clearAllFiles}
            disabled={disabled}
            className="px-4 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            清空列表
          </button>
        </div>
      )}
    </div>
  )
}

export default MultiFileUploader
